// App-menu & groepen: decide per group of app users which menu items they see
// in the mobile app. Hidden items are also refused by the server.
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { FaEdit, FaPlus, FaTrash, FaLock, FaEye, FaEyeSlash } from "react-icons/fa";
import Sidebar from "../../../components/Sidebar/Sidebar";
import Modal from "../../../components/Modal/Modal";
import "../../../components/AdminList/AdminList.css";
import "../roles/Roles.css";
import "./AppGroups.css";
import { AuthContext } from "../../../context/Authcontext";
import { getAppMenus, getAppGroups, createAppGroup, updateAppGroup, deleteAppGroup } from "../../../utils/api";

const emptyForm = { name: "", description: "", hiddenMenus: [] };

export default function AppGroups() {
  const { can } = useContext(AuthContext);
  const [groups, setGroups] = useState([]);
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isModalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [groupList, menuList] = await Promise.all([getAppGroups(), getAppMenus()]);
      setGroups(Array.isArray(groupList) ? groupList : []);
      setMenus(Array.isArray(menuList) ? menuList : []);
      setError("");
    } catch (err) {
      setError(err.message || "Kon groepen niet laden");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const menuSections = useMemo(() => {
    const map = new Map();
    menus.forEach((m) => {
      if (!map.has(m.group)) map.set(m.group, []);
      map.get(m.group).push(m);
    });
    return [...map.entries()];
  }, [menus]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = (group) => {
    setEditing(group);
    setForm({ name: group.name, description: group.description || "", hiddenMenus: group.hiddenMenus || [] });
    setModalOpen(true);
  };

  const toggleMenu = (id) => {
    setForm((prev) => ({
      ...prev,
      hiddenMenus: prev.hiddenMenus.includes(id)
        ? prev.hiddenMenus.filter((m) => m !== id)
        : [...prev.hiddenMenus, id],
    }));
  };

  const setSection = (items, visible) => {
    const ids = items.map((m) => m.id);
    setForm((prev) => ({
      ...prev,
      hiddenMenus: visible
        ? prev.hiddenMenus.filter((m) => !ids.includes(m))
        : [...new Set([...prev.hiddenMenus, ...ids])],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) await updateAppGroup(editing._id, form);
      else await createAppGroup(form);
      setModalOpen(false);
      await load();
    } catch (err) {
      alert(err.message || "Opslaan mislukt");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (group) => {
    if (!window.confirm(`Groep "${group.name}" verwijderen? Leden vallen terug op "Alle gebruikers".`)) return;
    try {
      await deleteAppGroup(group._id);
      await load();
    } catch (err) {
      alert(err.message || "Verwijderen mislukt");
    }
  };

  const canCreate = can("appGroups.create");
  const canUpdate = can("appGroups.update");
  const canDelete = can("appGroups.delete");
  const totalMenus = menus.length;

  return (
    <div className="dashboard-container">
      <Sidebar />
      <main className="dashboard-content">
        <div className="admin-list-container">
          <div className="admin-list-header">
            <h2>App-menu &amp; groepen</h2>
            <div className="header-actions">
              {canCreate && (
                <button className="add-btn" onClick={openCreate}>
                  <FaPlus /> Nieuwe groep
                </button>
              )}
            </div>
          </div>

          <p className="roles-intro">
            Bepaal per groep app-gebruikers welke menu-items zij in de app zien. Verborgen onderdelen worden ook door de
            server geweigerd. Iedereen zonder eigen groep valt onder <strong>Alle gebruikers</strong>; wie inlogt als gast
            valt onder <strong>Gasten</strong>. Een groep toewijzen doet u via <strong>Gebruikers</strong> → account
            bewerken → <em>App-groep</em>. Dashboard en Mijn profiel zijn altijd zichtbaar.
          </p>

          {loading && <div className="admin-list-loading"><div className="spinner"></div></div>}
          {error && <div className="admin-list-error">{error}</div>}

          {!loading && !error && (
            <div className="table-container">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Groep</th>
                    <th>Omschrijving</th>
                    <th>Zichtbare menu-items</th>
                    <th>Leden</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {groups.map((group) => (
                    <tr key={group._id}>
                      <td>
                        {group.name} {group.isSystem && <FaLock title="Systeemgroep (kan niet verwijderd worden)" />}
                      </td>
                      <td>{group.description || "-"}</td>
                      <td>{totalMenus - (group.hiddenMenus?.length || 0)} / {totalMenus}</td>
                      <td>{group.systemKey ? "—" : group.userCount || 0}</td>
                      <td className="actions">
                        {canUpdate && (
                          <button className="edit-btn" title="Bewerken" onClick={() => openEdit(group)}><FaEdit /></button>
                        )}
                        {canDelete && !group.isSystem && (
                          <button className="delete-btn" title="Verwijderen" onClick={() => handleDelete(group)}><FaTrash /></button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? `Groep bewerken: ${editing.name}` : "Nieuwe groep"}
        size="large"
      >
        <form onSubmit={handleSubmit}>
          <div className="form-section">
            <div className="form-group">
              <label>Naam *</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                required
                disabled={editing?.isSystem}
                placeholder="Bijv. Jongeren"
              />
            </div>
            <div className="form-group">
              <label>Omschrijving</label>
              <textarea
                rows="2"
                value={form.description}
                onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
              />
            </div>
          </div>

          <div className="form-section">
            <h3>Menu-items in de app</h3>
            <div className="appmenu-sections">
              {menuSections.map(([section, items]) => {
                const allVisible = items.every((m) => !form.hiddenMenus.includes(m.id));
                return (
                  <div key={section} className="appmenu-section">
                    <div className="appmenu-section-head">
                      <strong>{section}</strong>
                      <button type="button" className="link-btn" onClick={() => setSection(items, !allVisible)}>
                        {allVisible ? "Alles verbergen" : "Alles tonen"}
                      </button>
                    </div>
                    {items.map((m) => {
                      const visible = !form.hiddenMenus.includes(m.id);
                      return (
                        <button
                          type="button"
                          key={m.id}
                          className={`appmenu-item ${visible ? "on" : "off"}`}
                          onClick={() => toggleMenu(m.id)}
                        >
                          {visible ? <FaEye /> : <FaEyeSlash />}
                          <span>{m.label}</span>
                          <em>{visible ? "Zichtbaar" : "Verborgen"}</em>
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setModalOpen(false)}>Annuleren</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? "Opslaan..." : "Opslaan"}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
